import {
  FailedResErrorObjectType,
  SuccessResDataObjectType,
} from "@/types/dataRequestResuit";
import axios from "axios";
import store from "@/store";
import Vue from "vue";
import { Commit } from "vuex";

class CustomRequest {
  public static get = async <T>(request: {
    uri: string;
    params?: Record<string, unknown>;
    commit?: Commit;
    target?: string;
    flash?: boolean;
  }): Promise<SuccessResDataObjectType<T> | FailedResErrorObjectType> => {
    return CustomRequest.request({ method: "get", ...request });
  };

  public static post = async <T>(request: {
    uri: string;
    params?: Record<string, unknown>;
    commit?: Commit;
    target?: string;
    flash?: boolean;
  }): Promise<SuccessResDataObjectType<T> | FailedResErrorObjectType> => {
    return CustomRequest.request({ method: "post", ...request });
  };

  public static put = async <T>(request: {
    uri: string;
    params?: Record<string, unknown>;
    commit?: Commit;
    target?: string;
    flash?: boolean;
  }): Promise<SuccessResDataObjectType<T> | FailedResErrorObjectType> => {
    return CustomRequest.request({ method: "put", ...request });
  };

  public static patch = async <T>(request: {
    uri: string;
    params?: Record<string, unknown>;
    commit?: Commit;
    target?: string;
    flash?: boolean;
  }): Promise<SuccessResDataObjectType<T> | FailedResErrorObjectType> => {
    return CustomRequest.request({ method: "patch", ...request });
  };

  public static delete = async (request: {
    uri: string;
    flash?: boolean;
  }): Promise<{ error: boolean } | FailedResErrorObjectType> => {
    store.commit("error/resetError", null);
    try {
      const result = await axios.delete(request.uri);
      if (result.data && request.flash) {
        Vue.prototype.$flashStorage.flash(
          "処理が正常に完了しました",
          "success",
          {
            timeout: 3000,
            important: true,
          }
        );
        return {
          error: false,
        };
      } else {
        Vue.prototype.$flashStorage.flash(
          "エラーにより処理を完了できませんでした",
          "error",
          {
            timeout: 3000,
            important: true,
          }
        );
        return {
          error: true,
        };
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          store.commit("error/error", error.response.data);
        }
        if (request.flash) {
          Vue.prototype.$flashStorage.flash(
            "エラーにより処理を完了できませんでした",
            "error",
            {
              timeout: 3000,
              important: true,
            }
          );
        }
        return {
          data: null,
          error,
        };
      }
      throw error;
    }
  };

  private static request = async <T>(request: {
    method: string;
    uri: string;
    params?: Record<string, unknown>;
    commit?: Commit;
    target?: string;
    flash?: boolean;
  }): Promise<SuccessResDataObjectType<T> | FailedResErrorObjectType> => {
    store.commit("error/resetError", null);
    try {
      let data!: T;

      if (request.method === "get") {
        ({ data } = await axios.get<T>(request.uri, {
          params: request.params,
        }));
      } else if (request.method === "post") {
        ({ data } = await axios.post<T>(request.uri, request.params));
      } else if (request.method === "patch") {
        ({ data } = await axios.patch<T>(request.uri, request.params));
      } else if (request.method === "put") {
        ({ data } = await axios.put<T>(request.uri, request.params));
      }

      if (data) {
        if (request.commit && request.target) {
          request.commit(request.target, data);
        }
        if (request.flash) {
          Vue.prototype.$flashStorage.flash(
            "処理が正常に完了しました",
            "success",
            {
              timeout: 3000,
              important: true,
            }
          );
        }
      }

      return {
        data,
        error: null,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          store.commit("error/error", error.response.data);
        }
        if (request.flash) {
          Vue.prototype.$flashStorage.flash(
            "エラーにより処理を完了できませんでした",
            "error",
            {
              timeout: 3000,
              important: true,
            }
          );
        }
        return {
          data: null,
          error,
        };
      }
      throw error;
    }
  };
}

export default CustomRequest;
